import useGenreOptions from "../hooks/useGenreOptions";
// src/pages/VenuesIndex.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Grid,
  Container,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Rating,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StadiumIcon from "@mui/icons-material/Stadium";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import TuneIcon from "@mui/icons-material/Tune";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import PaidIcon from "@mui/icons-material/Paid";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { directoryService, profileService } from "../services/resources.service";
import { hasPermission, PERMISSIONS } from "../services/authRoles";
import { buildRecipientInboxPath } from "../services/messaging";
import { canContactVenue } from "../services/proFeatures";
import DiscoveryMap from "../components/DiscoveryMap";
import { ViewModeToggle } from "../components/DiscoveryActions";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { getDirectoryLocationQuery } from "../services/eventDiscovery";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import { formatMoneyRange, useLocale, useMessage } from "../i18n/locale";
import ProFeatureControl from "../components/ProFeatureControl";
import { BANNER_IMAGE_FALLBACK, getBannerImage, imageFallbackHandler } from "../services/profileImages";
import { fetchAllMapEntities } from "../services/mapEntities";
import useDebouncedSearch from "../hooks/useDebouncedSearch";

/**
 * GET /venues?query&genres[]&capacityBand=small|medium|large|any&budget=lt1500|1500-3000|gt3000|any
 * &facilities[]=pa|lights|backline|accessible&ratingMin&radiusKm&city&page&pageSize&openSoon=true|false
 */
async function fetchVenues({
  query,
  genres,
  capacityBand,
  budget,
  facilities,
  ratingMin,
  radiusKm,
  city,
  openSoon,
  page,
  pageSize,
  latitude,
  longitude,
}) {
  return directoryService.venues({ query, genres, capacityBand, budget, facilities, ratingMin, radiusKm, city, openSoon, page, pageSize, latitude, longitude });
}

// ---------------- UI bits ----------------
function CapacityChip({ cap }) {
  if (cap === null || cap === undefined || cap === "" || !Number.isFinite(Number(cap)) || Number(cap) <= 0) {
    return null;
  }

  return (
    <Chip
      size="small"
      icon={<PeopleAltIcon />}
      label={`${cap} cap`}
      variant="outlined"
    />
  );
}
function BudgetChip({ min, max, currency }) {
  const localeContext = useLocale();
  const hasMin = min !== null && min !== undefined && min !== "" && Number.isFinite(Number(min));
  const hasMax = max !== null && max !== undefined && max !== "" && Number.isFinite(Number(max));

  if (!hasMin && !hasMax) return null;

  return (
    <Chip
      size="small"
      icon={<PaidIcon />}
      label={formatMoneyRange(min, max, {
        locale: localeContext.locale,
        currency: currency || localeContext.currency,
      })}
      variant="outlined"
    />
  );
}

function VenueCard({
  venue,
  onOpen,
  onContact,
  onPostGig,
  onSetAvailability,
  contactLocked = false,
  contactLabel,
  postGigLabel,
  setAvailabilityLabel,
}) {
  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardActionArea onClick={() => onOpen?.(venue)} sx={{ flexGrow: 1 }}>
        <img
          src={getBannerImage(venue)}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={venue.name}
          loading="lazy"
          style={{
            width: "100%",
            height: 140,
            objectFit: "cover",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        />
        <CardContent>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <StadiumIcon />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={800} noWrap>{venue.name}</Typography>
                <LinkedAccountBadge ownerUserId={venue.ownerUserId} />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>
                {venue.city} • {venue.genresHosted?.slice(0, 3).join(", ")}
                {venue.genresHosted.length > 3 ? "…" : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: "wrap" }}>
            <Rating
              value={venue.rating || 0}
              precision={0.1}
              size="small"
              readOnly
              icon={<StarRoundedIcon fontSize="inherit" />}
              emptyIcon={<StarRoundedIcon fontSize="inherit" />}
            />
            <Typography variant="caption" color="text.secondary">
              {venue.rating?.toFixed?.(1) ?? "—"}
            </Typography>
            <CapacityChip cap={venue.capacity} />
            <BudgetChip min={venue.budgetMin} max={venue.budgetMax} currency={venue.currency} />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap" }}>
            {venue.facilities?.pa && <Chip size="small" icon={<VolumeUpIcon />} label="PA" />}
            {venue.facilities?.lights && <Chip size="small" icon={<EmojiObjectsIcon />} label="Lights" />}
            {venue.facilities?.backline && <Chip size="small" icon={<PrecisionManufacturingIcon />} label="Backline" />}
            {venue.facilities?.accessible && (
              <Chip size="small" icon={<AccessibilityNewIcon />} label="Accessible" />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      <CardActions
        sx={{
          px: 2,
          pb: 2,
          pt: 0,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(style) ~ :not(style)": { ml: 0, mt: 1 },
        }}
      >
        <Button fullWidth variant="contained" onClick={() => onOpen?.(venue)}>
          View venue
        </Button>
        {onPostGig && (
          <Button fullWidth variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => onPostGig(venue)}>
            {postGigLabel}
          </Button>
        )}
        {onSetAvailability && (
          <Button fullWidth variant="outlined" startIcon={<EventAvailableIcon />} onClick={() => onSetAvailability(venue)}>
            {setAvailabilityLabel}
          </Button>
        )}
        {onContact && (
          <ProFeatureControl locked={contactLocked} label={contactLabel}>
            <span>
              <Button fullWidth variant="outlined" disabled={contactLocked} onClick={() => onContact(venue)}>
                {contactLabel}
              </Button>
            </span>
          </ProFeatureControl>
        )}
      </CardActions>
    </Card>
  );
}

// ---------------- Page ----------------
export default function VenuesIndex({ onNavigate, onRequireAuth, onChangeRegion, discoveryLocation, discoveryRadiusKm, onChangeDiscoveryRadius, user, region = "Anywhere" }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const contactLabel = useMessage("venueContact");
  const postGigLabel = useMessage("venueEventsPostGig");
  const setAvailabilityLabel = useMessage("venueSetAvailability");
  const mayContactByRole = !user || hasPermission(user, PERMISSIONS.CONTACT_VENUE);
  const contactLocked = Boolean(user) && mayContactByRole && !canContactVenue(user);
  const [managedVenueIds, setManagedVenueIds] = useState(() => new Set());

  // Filters
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: query, submitSearch } = useDebouncedSearch();
  const [genres, setGenres] = useState(["All"]); // multi
  const [capacityBand, setCapacityBand] = useState("any"); // small|medium|large|any
  const [budget, setBudget] = useState("any"); // lt1500|1500-3000|gt3000|any
  const [facilities, setFacilities] = useState([]); // ['pa','lights','backline','accessible']
  const [ratingMin, setRatingMin] = useState(0);
  const [city, setCity] = useState(region);
  const radiusKm = discoveryRadiusKm;
  const setRadiusKm = onChangeDiscoveryRadius;
  const [openSoon, setOpenSoon] = useState(false);
  const [mapMode, setMapMode] = useState(false);

  // Data
  const [venues, setVenues] = useState([]);
  const [mapVenues, setMapVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;
  const loadMoreRef = useRef(null);

  // UI
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  useEffect(() => {
    let active = true;
    fetchAllMapEntities(directoryService.venues, {
      query,
      genres,
      capacityBand,
      budget,
      facilities,
      ratingMin,
      openSoon,
      ...getDirectoryLocationQuery(city, radiusKm, discoveryLocation),
    }).then((items) => {
      if (active) setMapVenues(items);
    });
    return () => { active = false; };
  }, [
    query,
    genres,
    capacityBand,
    budget,
    facilities,
    ratingMin,
    city,
    radiusKm,
    openSoon,
    discoveryLocation,
  ]);

  useEffect(() => {
    let active = true;
    setManagedVenueIds(new Set());
    if (user?.role !== "VENUE") return () => { active = false; };

    profileService.me()
      .then((profile) => {
        if (active) {
          setManagedVenueIds(new Set((profile?.venuePages || []).map((venue) => String(venue.id))));
        }
      })
      .catch(() => {
        if (active) setManagedVenueIds(new Set());
      });

    return () => { active = false; };
  }, [user?.id, user?.role]);

  const genreOptions = useGenreOptions();

  const toggleGenre = (g) => {
    setGenres((prev) => {
      if (g === "All") return ["All"];
      const s = new Set(prev.filter((x) => x !== "All"));
      s.has(g) ? s.delete(g) : s.add(g);
      return Array.from(s);
    });
  };

  const toggleFacility = (f) => {
    setFacilities((prev) => {
      const s = new Set(prev);
      s.has(f) ? s.delete(f) : s.add(f);
      return Array.from(s);
    });
  };

  const runFetch = async ({ reset = false } = {}) => {
    setLoading(true);
    const nextPage = reset ? 1 : page;
    const res = await fetchVenues({
      query,
      genres,
      capacityBand,
      budget,
      facilities,
      ratingMin,
      radiusKm,
      ...getDirectoryLocationQuery(city, radiusKm, discoveryLocation),
      openSoon,
      page: nextPage,
      pageSize,
    });
    setLoading(false);
    setHasMore(res.hasMore);
    if (reset) {
      setPage(1);
      setVenues(res.data);
    } else {
      setVenues((prev) => [...prev, ...res.data]);
    }
  };

  useEffect(() => {
    runFetch({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    JSON.stringify(genres),
    capacityBand,
    budget,
    JSON.stringify(facilities),
    ratingMin,
    city,
    radiusKm,
    openSoon,
    discoveryLocation,
  ]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || loading || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) target.click(); },
      { rootMargin: "240px 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, page]);

  const subtitle = useMemo(() => {
    const parts = [region];
    if (city !== "Anywhere") parts.push(city);
    parts.push(radiusLabel(radiusKm));
    if (genres.length && !genres.includes("All")) parts.push(genres.join(", "));
    if (capacityBand !== "any")
      parts.push(capacityBand === "small" ? "≤150 cap" : capacityBand === "medium" ? "151–400 cap" : ">400 cap");
    if (budget !== "any")
      parts.push(budget === "lt1500" ? "< R1,500" : budget === "gt3000" ? "> R3,000" : "R1,500–R3,000");
    if (facilities.length) parts.push(facilities.join("+"));
    if (openSoon) parts.push("Open dates soon");
    if (ratingMin) parts.push(`≥ ${ratingMin.toFixed(1)}★`);
    return parts.join(" • ");
  }, [region, city, radiusKm, genres, capacityBand, budget, facilities, openSoon, ratingMin]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      {/* Page AppBar */}
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => onNavigate?.("/")}>
            <ArrowBackIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Venues
          </Typography>

          <Box component="form" onSubmit={submitSearch} sx={{ flex: 1, mx: 1 }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search venues"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Map/List toggle */}
          <ViewModeToggle
            mapMode={mapMode}
            onChange={setMapMode}
            sx={{ display: { xs: "none", sm: "inline-flex" }, mr: 1 }}
          />

          <Tooltip title="More filters">
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="filters">
              <TuneIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Filter chips */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            {/* City & radius */}
            <Chip
              icon={<LocationOnIcon />}
              label={city}
              onClick={async () => {
                if (city === "Near me" && radiusKm !== INFINITE_RADIUS) {
                  setRadiusKm(INFINITE_RADIUS);
                  return;
                }
                if (discoveryLocation) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                  return;
                }
                const location = await onChangeRegion?.();
                if (location) {
                  setCity("Near me");
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                }
              }}
              variant={city === "Near me" && radiusKm !== INFINITE_RADIUS ? "filled" : "outlined"}
            />
            {[2, 10, 25, 100, null].map((km) => (
              <Chip
                key={km ?? "infinite"}
                label={radiusLabel(km)}
                variant={radiusKm === km ? "filled" : "outlined"}
                onClick={() => setRadiusKm(km)}
                size="small"
              />
            ))}

            {/* Genres */}
            {genreOptions.map((g) => (
              <Chip
                key={g}
                label={g}
                variant={
                  genres.includes(g) || (g === "All" && genres.length === 1) ? "filled" : "outlined"
                }
                onClick={() => toggleGenre(g)}
                size="small"
              />
            ))}

            {/* Capacity bands */}
            {[
              { key: "any", label: "Any size" },
              { key: "small", label: "Small ≤150" },
              { key: "medium", label: "Medium 151–400" },
              { key: "large", label: "Large >400" },
            ].map((o) => (
              <Chip
                key={o.key}
                label={o.label}
                variant={capacityBand === o.key ? "filled" : "outlined"}
                onClick={() => setCapacityBand(o.key)}
                size="small"
              />
            ))}

            {/* Budget */}
            {[
              { key: "any", label: "Any budget" },
              { key: "lt1500", label: "< R1,500" },
              { key: "1500-3000", label: "R1,500–R3,000" },
              { key: "gt3000", label: "> R3,000" },
            ].map((b) => (
              <Chip
                key={b.key}
                label={b.label}
                variant={budget === b.key ? "filled" : "outlined"}
                onClick={() => setBudget(b.key)}
                size="small"
              />
            ))}

            {/* Facilities */}
            {[
              { key: "pa", label: "PA", icon: <VolumeUpIcon /> },
              { key: "lights", label: "Lights", icon: <EmojiObjectsIcon /> },
              { key: "backline", label: "Backline", icon: <PrecisionManufacturingIcon /> },
              { key: "accessible", label: "Accessible", icon: <AccessibilityNewIcon /> },
            ].map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                icon={f.icon}
                variant={facilities.includes(f.key) ? "filled" : "outlined"}
                onClick={() => toggleFacility(f.key)}
                size="small"
              />
            ))}

            {/* Rating thresholds */}
            {[0, 3.5, 4.0, 4.5].map((r) => (
              <Chip
                key={r}
                icon={<StarRoundedIcon />}
                label={r === 0 ? "All ratings" : `≥ ${r}`}
                variant={ratingMin === r ? "filled" : "outlined"}
                onClick={() => setRatingMin(r)}
                size="small"
              />
            ))}

            {/* Open dates soon */}
            <Chip
              label="Open dates soon"
              variant={openSoon ? "filled" : "outlined"}
              onClick={() => setOpenSoon((s) => !s)}
              size="small"
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {subtitle ? `Showing: ${subtitle}` : "Showing all venues"}
          </Typography>
        </Stack>

        {/* Map */}
        {mapMode && (
          <Box sx={{ mb: 2 }}>
            <DiscoveryMap
              items={mapVenues}
              getCoordinates={(venue) => venue.geo}
              getLabel={(venue) => venue.name}
              onSelect={(venue) => onNavigate?.(`/venues/${venue.id}`)}
              labelMessageKey="venueMapLabel"
              height={320}
            />
          </Box>
        )}

        {/* Grid */}
        <Grid container spacing={2}>
          {(loading && venues.length === 0 ? Array.from({ length: 8 }) : venues).map((v, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={v?.id ?? idx}>
              {v ? (
                <VenueCard
                  venue={v}
                  onOpen={(venue) => onNavigate?.(`/venues/${venue.id}`)}
                  onPostGig={managedVenueIds.has(String(v.id))
                    ? (venue) => onNavigate?.(`/venues/gig/new?venueId=${encodeURIComponent(venue.id)}`)
                    : undefined}
                  onSetAvailability={managedVenueIds.has(String(v.id))
                    ? (venue) => onNavigate?.(`/venues/${encodeURIComponent(venue.id)}/edit#availability`)
                    : undefined}
                  postGigLabel={postGigLabel}
                  setAvailabilityLabel={setAvailabilityLabel}
                  onContact={mayContactByRole
                    ? (venue) => {
                      const redirectTo = buildRecipientInboxPath("venue", venue.id);
                      if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
                      onNavigate?.(redirectTo);
                    }
                    : undefined}
                  contactLocked={contactLocked}
                  contactLabel={contactLabel}
                />
              ) : (
                <Skeleton variant="rounded" height={230} sx={{ borderRadius: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Stack alignItems="center" sx={{ py: 3 }}>
          {hasMore ? (
            <Button
              ref={loadMoreRef}
              size="large"
              disabled={loading}
              onClick={async () => {
                setPage((p) => p + 1);
                setLoading(true);
                const res = await fetchVenues({
                  query,
                  genres,
                  capacityBand,
                  budget,
                  facilities,
                  ratingMin,
                  radiusKm,
                  city,
                  openSoon,
                  page: page + 1,
                  pageSize,
                });
                setLoading(false);
                setHasMore(res.hasMore);
                setVenues((prev) => [...prev, ...res.data]);
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You’ve reached the end.
            </Typography>
          )}
        </Stack>
      </Container>

      {/* More filters menu (placeholder for future expansion) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        MenuListProps={{ "aria-labelledby": "more-filters-button" }}
      >
        <MenuItem disabled>More filters coming soon…</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setGenres(["All"]);
            setCapacityBand("any");
            setBudget("any");
            setFacilities([]);
            setRatingMin(0);
            setCity("Anywhere");
            setRadiusKm(INFINITE_RADIUS);
            setOpenSoon(false);
            setMenuAnchor(null);
          }}
        >
          Reset all
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <Box sx={{ height: 16 }} />
    </Box>
  );
}
